/**
 * Local, isolated session store for the WAF Monetization demo — a scoped-down port
 * of the original demo's session model. Each session is fully persisted in
 * localStorage (messages, agent-activity events, spend, discovery snapshot) and
 * restored on switch, so sessions are real and isolated. Title is auto-derived from
 * the first user message (like Claude/GPT). 7-day TTL: stale sessions are pruned.
 *
 * Shape per session:
 *   { sessionId, title, created, lastSeen, snapshot: { messages, events, spent } }
 */

const KEY = 'x402demo.sessions.v2';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function newSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `s_${crypto.randomUUID()}`;
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function writeAll(obj) {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
  } catch {
    /* storage full / disabled — non-fatal */
  }
}

/** Prune sessions older than the TTL. */
function prune(obj, now) {
  let changed = false;
  for (const id of Object.keys(obj)) {
    if (now - (obj[id].lastSeen ?? 0) > TTL_MS) {
      delete obj[id];
      changed = true;
    }
  }
  return changed;
}

/** List sessions (newest first), pruning expired ones. */
export function listSessions() {
  const obj = readAll();
  const now = Date.now();
  if (prune(obj, now)) writeAll(obj);
  return Object.values(obj)
    .map((s) => ({ sessionId: s.sessionId, title: s.title, label: s.title, lastSeen: s.lastSeen, created: s.created }))
    .sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0));
}

/** Ensure a session exists (no snapshot change), stamping lastSeen. */
export function touchSession(sessionId, title) {
  if (!sessionId) return;
  const obj = readAll();
  const now = Date.now();
  const s = obj[sessionId] || { sessionId, created: now, snapshot: { messages: [], events: [], spent: 0 } };
  s.lastSeen = now;
  if (title && !s.title) s.title = title;
  if (!s.title) s.title = defaultTitle(now);
  obj[sessionId] = s;
  writeAll(obj);
}

/** Persist the full snapshot for a session; auto-derive the title from msg 1. */
export function saveSnapshot(sessionId, snapshot) {
  if (!sessionId) return;
  const obj = readAll();
  const now = Date.now();
  const s = obj[sessionId] || { sessionId, created: now };
  s.lastSeen = now;
  s.snapshot = snapshot;
  // Title from the first user message, else keep/derive a default.
  const firstUser = (snapshot.messages || []).find((m) => m.role === 'user');
  if (firstUser && (!s.title || s.title.startsWith('Session '))) {
    s.title = firstUser.text.slice(0, 48) + (firstUser.text.length > 48 ? '…' : '');
  }
  if (!s.title) s.title = defaultTitle(now);
  obj[sessionId] = s;
  writeAll(obj);
}

/** Load a session's snapshot (or null if unknown). */
export function loadSnapshot(sessionId) {
  const s = readAll()[sessionId];
  return s ? s.snapshot || { messages: [], events: [], spent: 0 } : null;
}

export function renameSession(sessionId, title) {
  const obj = readAll();
  if (obj[sessionId]) {
    obj[sessionId].title = title;
    writeAll(obj);
  }
}

export function deleteSession(sessionId) {
  const obj = readAll();
  delete obj[sessionId];
  writeAll(obj);
}

export function deleteAllSessions() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* non-fatal */
  }
}

function defaultTitle(ts) {
  const d = new Date(ts);
  return `Session ${d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
}
